import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from '../ui/Modal';
import { 
  Input, 
  Textarea, 
  Select, 
  Checkbox, 
  FormButtons 
} from '../ui/FormComponents';
import {
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const EditCustomer = ({ isOpen, onClose, customer, onCustomerUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    type: 'b2c',
    status: 'active',
    // B2B specific fields
    company_id: '',
    company_name: '',
    job_title: '',
    tax_id: '',
    // Address
    street: '',
    city: '',
    postal_code: '',
    country: 'Israel',
    // Additional
    notes: '',
    newsletter_subscribed: false,
    marketing_consent: false
  });
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [companies, setCompanies] = useState([]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies');
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const loadCustomerData = async () => {
    try {
      // Try to fetch complete customer data from API
      const response = await axios.get(`/api/users/${customer.id}`).catch(() => {
        // Fallback to provided customer data
        return { data: { user: customer } };
      });

      const customerData = response.data.user || response.data;
      
      setFormData({
        first_name: customerData.first_name || '',
        last_name: customerData.last_name || '',
        email: customerData.email || '',
        phone: customerData.phone || '',
        type: customerData.role || 'b2c',
        status: customerData.status || 'active',
        company_id: customerData.company_id || '',
        company_name: customerData.company?.name || '',
        job_title: customerData.job_title || '',
        tax_id: customerData.tax_id || '',
        street: customerData.street || '',
        city: customerData.city || '',
        postal_code: customerData.postal_code || '',
        country: customerData.country || 'Israel',
        notes: customerData.notes || '',
        newsletter_subscribed: customerData.newsletter_subscribed || false,
        marketing_consent: customerData.marketing_consent || false
      });

      setErrors({});
      setHasChanges(false);

    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  };

  // Load customer data and companies when modal opens
  useEffect(() => {
    if (isOpen && customer) {
      loadCustomerData();
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customer]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };
      setHasChanges(true);
      return updated;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.first_name.trim()) newErrors.first_name = 'שם פרטי נדרש';
    if (!formData.last_name.trim()) newErrors.last_name = 'שם משפחה נדרש';
    if (!formData.email.trim()) newErrors.email = 'אימייל נדרש';
    if (!formData.phone.trim()) newErrors.phone = 'מספר טלפון נדרש';
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'כתובת אימייל לא תקינה';
    }

    // Phone validation (basic Israeli phone format)
    if (formData.phone && !/^[+]?[0-9\-\s()]{8,15}$/.test(formData.phone)) {
      newErrors.phone = 'מספר טלפון לא תקין';
    }

    // B2B specific validations
    if (formData.type === 'b2b') {
      if (!formData.company_id) newErrors.company_id = 'חברה נדרשת עבור לקוח B2B';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!hasChanges) {
      alert('לא בוצעו שינויים');
      return;
    }

    setLoading(true);

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        role: formData.type, // Map frontend 'type' to backend 'role'
        full_name: `${formData.first_name} ${formData.last_name}`.trim()
      };
      
      // Remove the frontend 'type' and 'company_name' fields to avoid confusion
      delete submitData.type;
      delete submitData.company_name;

      // Submit to API
      const response = await axios.put(`/api/users/${customer.id}`, submitData);

      if (response.data.user) {
        // Success notification
        alert('הלקוח עודכן בהצלחה!');
        
        // Notify parent component
        if (onCustomerUpdated) {
          onCustomerUpdated(response.data.user);
        }
        
        setHasChanges(false);
        onClose();
      }

    } catch (error) {
      console.error('Error updating customer:', error);
      alert('שגיאה בעדכון הלקוח. אנא נסה שנית.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (hasChanges && !loading) {
      if (window.confirm('יש שינויים שלא נשמרו. האם אתה בטוח שברצונך לסגור?')) {
        setHasChanges(false);
        onClose();
      }
    } else if (!loading) {
      onClose();
    }
  };

  const resetForm = () => {
    loadCustomerData();
    fetchCompanies(); // Refresh companies list too
    setHasChanges(false);
  };

  if (!customer) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`עריכת לקוח: ${customer.first_name} ${customer.last_name}`}
      size="lg"
      preventClose={loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Changed Data Warning */}
        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 ml-2" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">יש שינויים שטרם נשמרו</p>
                <p>אל תשכח לשמור את השינויים לפני סגירת החלון.</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Customer Type */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-3">סוג לקוח</label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="type"
                value="b2c"
                checked={formData.type === 'b2c'}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <UserIcon className="h-5 w-5 text-green-500 mr-2 ml-2" />
              <span className="text-sm text-gray-700">לקוח פרטי (B2C)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="type"
                value="b2b"
                checked={formData.type === 'b2b'}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <BuildingOfficeIcon className="h-5 w-5 text-purple-500 mr-2 ml-2" />
              <span className="text-sm text-gray-700">לקוח עסקי (B2B)</span>
            </label>
          </div>
        </div>

        {/* Basic Information */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <UserIcon className="h-5 w-5 ml-2" />
            פרטים אישיים
          </h4>
          
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="שם פרטי"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
              error={errors.first_name}
              placeholder="הזן שם פרטי..."
            />

            <Input
              label="שם משפחה"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
              error={errors.last_name}
              placeholder="הזן שם משפחה..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="כתובת אימייל"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              error={errors.email}
              placeholder="example@email.com"
              icon={EnvelopeIcon}
            />

            <Input
              label="מספר טלפון"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              required
              error={errors.phone}
              placeholder="+972-50-1234567"
              icon={PhoneIcon}
            />
          </div>
        </div>

        {/* B2B Specific Fields */}
        {formData.type === 'b2b' && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 ml-2" />
              פרטי חברה
            </h4>
            
            <Select
              label="חברה"
              name="company_id"
              value={formData.company_id}
              onChange={handleInputChange}
              required={formData.type === 'b2b'}
              error={errors.company_id}
              options={[
                { value: '', label: 'בחר חברה...' },
                ...companies.map(company => ({
                  value: company.id,
                  label: `${company.name} (${company.business_number})`
                }))
              ]}
            />

            {/* Show current company name if selected */}
            {formData.company_name && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-blue-500 ml-2" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">חברה נוכחית</p>
                    <p className="text-sm text-blue-700">{formData.company_name}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="תפקיד"
                name="job_title"
                value={formData.job_title}
                onChange={handleInputChange}
                placeholder="מנהל רכישות, מנכ״ל..."
              />

              <Input
                label="מספר עוסק/חברה"
                name="tax_id"
                value={formData.tax_id}
                onChange={handleInputChange}
                placeholder="123456789"
              />
            </div>
          </div>
        )}

        {/* Address Information */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <MapPinIcon className="h-5 w-5 ml-2" />
            כתובת
          </h4>
          
          <Input
            label="רחוב"
            name="street"
            value={formData.street}
            onChange={handleInputChange}
            placeholder="רחוב הרצל 123"
          />

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="עיר"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="תל אביב"
            />

            <Input
              label="מיקוד"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
              placeholder="61231"
            />

            <Select
              label="מדינה"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              options={[
                { value: 'Israel', label: 'ישראל' },
                { value: 'Palestine', label: 'פלסטין' },
                { value: 'Jordan', label: 'ירדן' },
                { value: 'Lebanon', label: 'לבנון' }
              ]}
            />
          </div>
        </div>

        {/* Additional Settings */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
            הגדרות נוספות
          </h4>
          
          <Select
            label="סטטוס לקוח"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            options={[
              { value: 'active', label: 'פעיל' },
              { value: 'inactive', label: 'לא פעיל' }
            ]}
          />

          <Textarea
            label="הערות"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            placeholder="הערות נוספות על הלקוח..."
          />

          <div className="space-y-3">
            <Checkbox
              label="הרשמה לניוזלטר"
              name="newsletter_subscribed"
              checked={formData.newsletter_subscribed}
              onChange={handleInputChange}
              help="הלקוח יקבל עדכונים על מוצרים חדשים והצעות מיוחדות"
            />

            <Checkbox
              label="הסכמה לשיווק"
              name="marketing_consent"
              checked={formData.marketing_consent}
              onChange={handleInputChange}
              help="הסכמה לקבלת חומרי פרסום ושיווק"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={resetForm}
              disabled={loading || !hasChanges}
              className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              אפס שינויים
            </button>
          </div>

          <FormButtons
            onCancel={handleClose}
            onSubmit={handleSubmit}
            submitText={hasChanges ? "שמור שינויים" : "אין שינויים"}
            cancelText="ביטול"
            loading={loading}
            submitDisabled={!hasChanges || Object.keys(errors).length > 0}
            showCancel={true}
          />
        </div>
      </form>
    </Modal>
  );
};

export default EditCustomer;
